import { api, apiBlob, apiUpload } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  DispatchableDocumentOption,
  DispatchedDocumentDetail,
  DispatchedDocumentListItem,
  DispatchedDocumentListQuery,
  DispatchedDocumentRequest,
  DocumentRouteItem,
  InboxListQuery,
  ReceivedDocumentDetail,
  ReceivedDocumentListItem,
  ReceivedDocumentListQuery,
  ReceivedDocumentRequest,
  RouteDocumentRequest,
  RoutingAttachmentItem,
  RoutingDesk,
  RoutingDocumentTypeListItem,
  RoutingDocumentTypeOption,
  RoutingDocumentTypeRequest,
  RoutingUnitOption,
} from './routingTypes';

/**
 * Serializes a list query.
 *
 * Every facet is written whenever its KEY IS PRESENT, empty string included — an empty value is
 * the reader saying "none of these", which matches no row. A truthiness check would drop it and
 * the server would read the axis as unmentioned.
 */
function serialize(query: ListQuery & Record<string, string | number | undefined>, facets: string[] = []): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);

  for (const facet of facets) {
    const value = query[facet];
    if (value !== undefined) params.set(facet, String(value));
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/** The route half every Routing module exposes the same way — the unit feed and the lines. */
function routeApi(base: string) {
  return {
    units: (signal?: AbortSignal) => api<RoutingUnitOption[]>(`${base}/units`, { signal }),

    routes: (id: string, query: ListQuery, signal?: AbortSignal) =>
      api<PagedResult<DocumentRouteItem>>(`${base}/${id}/routes${serialize({ ...query })}`, { signal }),
  };
}

/** The route's lines, read-only — what every profile's Routing tab lists. */
export type RoutingRouteReadApi = ReturnType<typeof routeApi>;

/**
 * Moving a document on — Receiving and the Inbox. A dispatch is not moved from its own register:
 * once it reaches a unit, that unit's Inbox moves it.
 */
function movementApi(base: string) {
  return {
    route: (id: string, body: RouteDocumentRequest) =>
      api<DocumentRouteItem>(`${base}/${id}/routes`, { method: 'POST', body }),

    acknowledge: (id: string, routeId: string) =>
      api<DocumentRouteItem>(`${base}/${id}/routes/${routeId}/acknowledge`, { method: 'POST' }),

    cancelForward: (id: string, routeId: string) => api<void>(`${base}/${id}/routes/${routeId}`, { method: 'DELETE' }),

    /** Where the caller sits for routing — their post's unit, or nothing. */
    desk: (signal?: AbortSignal) => api<RoutingDesk>(`${base}/me`, { signal }),

    /** A forward's files — anyone who may read; the bytes need `download`. */
    routeAttachments: (id: string, routeId: string, query: ListQuery, signal?: AbortSignal) =>
      api<PagedResult<RoutingAttachmentItem>>(`${base}/${id}/routes/${routeId}/attachments${serialize({ ...query })}`, { signal }),

    /** Files one file against a forward — the sending unit, while it is open. A REAL progress figure. */
    attachToRoute: (id: string, routeId: string, file: File, onProgress?: (percent: number) => void) => {
      const form = new FormData();
      form.append('file', file);

      return apiUpload<RoutingAttachmentItem>(`${base}/${id}/routes/${routeId}/attachments`, form, { onProgress });
    },

    downloadRouteAttachment: (id: string, routeId: string, attachmentId: string, signal?: AbortSignal) =>
      apiBlob(`${base}/${id}/routes/${routeId}/attachments/${attachmentId}`, { signal }),
  };
}

/**
 * One forward's files in the shape the preview panel and the filing helper read — the record and
 * the line bound in, so a route's files reuse the panel a dispatch's files use.
 */
export function routeFilesApi(movement: RoutingMovementApi, recordId: string, routeId: string) {
  return {
    attachments: (_id: string, query: ListQuery, signal?: AbortSignal) => movement.routeAttachments(recordId, routeId, query, signal),
    download: (_id: string, attachmentId: string, signal?: AbortSignal) =>
      movement.downloadRouteAttachment(recordId, routeId, attachmentId, signal),
    attach: (_id: string, file: File, onProgress?: (percent: number) => void) =>
      movement.attachToRoute(recordId, routeId, file, onProgress),
  };
}

/** The shape the route panel's verbs and the Route form are written against. */
export type RoutingMovementApi = ReturnType<typeof movementApi>;

/** Reading a record's files — the list, and the bytes. */
function fileReadApi(base: string) {
  return {
    attachments: (id: string, query: ListQuery, signal?: AbortSignal) =>
      api<PagedResult<RoutingAttachmentItem>>(`${base}/${id}/attachments${serialize({ ...query })}`, { signal }),

    /**
     * The file's bytes. A FETCH then an object URL, never an `<a href>` or an `<iframe src>` at
     * the endpoint: the token travels in a header no navigation sends, the API has its own
     * origin, and the response is served as an attachment on purpose.
     */
    download: (id: string, attachmentId: string, signal?: AbortSignal) =>
      apiBlob(`${base}/${id}/attachments/${attachmentId}`, { signal }),
  };
}

/** Filing, deleting and restoring a record's files — Dispatching's alone. */
function fileWriteApi(base: string) {
  return {
    /**
     * Files one soft copy or scan. Through `apiUpload` because the form shows a REAL progress
     * figure — `fetch` has no upload-progress event.
     */
    attach: (id: string, file: File, onProgress?: (percent: number) => void) => {
      const form = new FormData();
      form.append('file', file);

      return apiUpload<RoutingAttachmentItem>(`${base}/${id}/attachments`, form, { onProgress });
    },

    removeAttachment: (id: string, attachmentId: string) =>
      api<void>(`${base}/${id}/attachments/${attachmentId}`, { method: 'DELETE' }),

    restoreAttachment: (id: string, attachmentId: string) =>
      api<void>(`${base}/${id}/attachments/${attachmentId}/restore`, { method: 'POST' }),
  };
}

export type RoutingFileReadApi = ReturnType<typeof fileReadApi>;

export type RoutingFileApi = RoutingFileReadApi & ReturnType<typeof fileWriteApi>;

const RECEIVED = '/api/received-documents';

/** Hard copies received, and every unit each went to. Receiving files nothing. */
export const receivedDocumentsApi = {
  ...routeApi(RECEIVED),
  ...movementApi(RECEIVED),

  list: (query: ReceivedDocumentListQuery, signal?: AbortSignal) =>
    api<PagedResult<ReceivedDocumentListItem>>(
      `${RECEIVED}${serialize({ ...query }, ['routing', 'documentType', 'sender', 'holder'])}`,
      { signal },
    ),

  get: (id: string, signal?: AbortSignal) => api<ReceivedDocumentDetail>(`${RECEIVED}/${id}`, { signal }),

  create: (body: ReceivedDocumentRequest) => api<ReceivedDocumentDetail>(RECEIVED, { method: 'POST', body }),

  update: (id: string, body: ReceivedDocumentRequest) =>
    api<ReceivedDocumentDetail>(`${RECEIVED}/${id}`, { method: 'PUT', body }),

  remove: (id: string) => api<void>(`${RECEIVED}/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`${RECEIVED}/${id}/restore`, { method: 'POST' }),
};

const DISPATCHED = '/api/dispatched-documents';

/** Documents sent out — to a unit's Inbox or an outside office — and where each went. */
export const dispatchedDocumentsApi = {
  ...routeApi(DISPATCHED),
  ...fileReadApi(DISPATCHED),
  ...fileWriteApi(DISPATCHED),

  list: (query: DispatchedDocumentListQuery, signal?: AbortSignal) =>
    api<PagedResult<DispatchedDocumentListItem>>(
      `${DISPATCHED}${serialize({ ...query }, ['documentType', 'medium', 'mode', 'origin', 'source', 'destination'])}`,
      { signal },
    ),

  get: (id: string, signal?: AbortSignal) => api<DispatchedDocumentDetail>(`${DISPATCHED}/${id}`, { signal }),

  create: (body: DispatchedDocumentRequest) => api<DispatchedDocumentDetail>(DISPATCHED, { method: 'POST', body }),

  update: (id: string, body: DispatchedDocumentRequest) =>
    api<DispatchedDocumentDetail>(`${DISPATCHED}/${id}`, { method: 'PUT', body }),

  remove: (id: string) => api<void>(`${DISPATCHED}/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`${DISPATCHED}/${id}/restore`, { method: 'POST' }),

  /** Received documents on hand with a unit — this module's own feed, on `create`. */
  dispatchable: (signal?: AbortSignal) => api<DispatchableDocumentOption[]>(`${DISPATCHED}/dispatchable`, { signal }),
};

const INBOX = '/api/inbox';

/**
 * Dispatches addressed to a unit, read from the receiving side. The Inbox owns no record — an
 * item IS the dispatch — so it takes one in, moves it on, and reads its files; it never edits it.
 */
export const inboxApi = {
  ...routeApi(INBOX),
  ...movementApi(INBOX),
  ...fileReadApi(INBOX),

  list: (query: InboxListQuery, signal?: AbortSignal) =>
    api<PagedResult<DispatchedDocumentListItem>>(
      `${INBOX}${serialize({ ...query }, ['routing', 'recipient', 'holder', 'documentType'])}`,
      { signal },
    ),

  get: (id: string, signal?: AbortSignal) => api<DispatchedDocumentDetail>(`${INBOX}/${id}`, { signal }),

  /** Takes it in at the recipient unit — the dispatch line's acknowledgement. */
  receive: (id: string) => api<DocumentRouteItem>(`${INBOX}/${id}/receive`, { method: 'POST' }),
};

const TYPES = '/api/routing-document-types';

/** What a routed document is — the catalog behind Routing › Settings. */
export const routingDocumentTypesApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<RoutingDocumentTypeListItem>>(`${TYPES}${serialize({ ...query })}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<RoutingDocumentTypeListItem>(`${TYPES}/${id}`, { signal }),

  options: (signal?: AbortSignal) => api<RoutingDocumentTypeOption[]>(`${TYPES}/options`, { signal }),

  create: (body: RoutingDocumentTypeRequest) => api<RoutingDocumentTypeListItem>(TYPES, { method: 'POST', body }),

  update: (id: string, body: RoutingDocumentTypeRequest) =>
    api<RoutingDocumentTypeListItem>(`${TYPES}/${id}`, { method: 'PUT', body }),

  remove: (id: string) => api<void>(`${TYPES}/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`${TYPES}/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>(`${TYPES}/clear-data`, { method: 'POST' }),
};
